'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Container } from '@eliadmin/core/src/components/ui/container';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

const MOBILE_COLLAPSED_LINES = 2;
const DESKTOP_COLLAPSED_LINES = 3;

function getTextNodes(element: HTMLElement) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  const textNodes: Text[] = [];

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  return textNodes;
}

function measureCollapsedState(element: HTMLElement, collapsedLines: number) {
  const elementTop = element.getBoundingClientRect().top;
  const lineBottoms: number[] = [];

  for (const textNode of getTextNodes(element)) {
    // A text range already returns one rectangle per wrapped line. Measuring
    // individual characters repeats the same layout read thousands of times.
    const range = document.createRange();
    range.selectNodeContents(textNode);

    for (const rect of Array.from(range.getClientRects())) {
      if (rect.height === 0) continue;

      const relativeBottom = Math.ceil(rect.bottom - elementTop);
      const matchesExistingLine = lineBottoms.some(
        bottom => Math.abs(bottom - relativeBottom) <= 1,
      );

      if (!matchesExistingLine) {
        lineBottoms.push(relativeBottom);
      }
    }
  }

  lineBottoms.sort((first, second) => first - second);

  if (lineBottoms.length >= collapsedLines) {
    return {
      collapsedHeight: lineBottoms[collapsedLines - 1],
      isOverflowing: lineBottoms.length > collapsedLines,
    };
  }

  const style = window.getComputedStyle(element);
  const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.2;
  const fallbackHeight = Math.ceil(lineHeight * collapsedLines);

  return {
    collapsedHeight: fallbackHeight,
    isOverflowing: element.scrollHeight > fallbackHeight + 1,
  };
}

export function CategoryDescriptionDisclosure({ children }: { children: ReactNode }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [children]);

  useEffect(() => {
    const checkDescriptionOverflow = () => {
      const element = descriptionRef.current;

      if (!element) {
        setCollapsedHeight(null);
        setIsOverflowing(false);
        return;
      }

      const collapsedLines = window.matchMedia('(min-width: 640px)').matches
        ? DESKTOP_COLLAPSED_LINES
        : MOBILE_COLLAPSED_LINES;
      const nextState = measureCollapsedState(element, collapsedLines);

      setCollapsedHeight(nextState.collapsedHeight);
      setIsOverflowing(nextState.isOverflowing);
    };

    checkDescriptionOverflow();

    const resizeObserver = new ResizeObserver(checkDescriptionOverflow);
    if (descriptionRef.current) {
      resizeObserver.observe(descriptionRef.current);
    }

    window.addEventListener('resize', checkDescriptionOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkDescriptionOverflow);
    };
  }, [children]);

  return (
    <Container className="relative mb-10 w-full">
      <div
        ref={descriptionRef}
        className={cn(
          'w-full text-left leading-6 text-[#838383] transition-[max-height] duration-300 ease-in-out',
          !isExpanded && 'max-h-12 overflow-hidden sm:max-h-[72px]',
        )}
        style={!isExpanded && collapsedHeight ? { maxHeight: `${collapsedHeight}px` } : undefined}
      >
        {children}
      </div>

      {!isExpanded && isOverflowing && (
        <div className="absolute -bottom-8 left-0 right-0 flex justify-center sm:-bottom-8">
          <div
            className="pointer-events-none absolute bottom-0 h-12 w-full bg-gradient-to-t from-white via-white to-transparent"
            style={{ zIndex: 1 }}
          />
          <Button
            variant="link"
            className="relative max-h-8 py-0"
            style={{ zIndex: 2 }}
            onClick={() => setIsExpanded(true)}
          >
            {'Celý popis'}
          </Button>
        </div>
      )}

      {isExpanded && (
        <div className="mt-2 flex justify-center">
          <Button variant="link" className="max-h-8 py-0" onClick={() => setIsExpanded(false)}>
            {'Skryť popis'}
          </Button>
        </div>
      )}
    </Container>
  );
}
